import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import CountryPicker from "@/v2-backoffice-client/country/CountryPicker"
import ProjectOption from "@/v2-backoffice-client/project/ProjectOption"
import { NO_PROJECT_VALUE } from "@/v2-backoffice-client/project/project.constants"
import type { TenantDefaultsContext } from "@/v2-backoffice-client/tenant/update/tenant.update.hooks"

export type TenantDefaultsFieldsProps = {
  // The two fields are one coupled state machine (the project settles the
  // country), so they arrive as the one object useTenantDefaultsValues
  // returns rather than loose props.
  context: TenantDefaultsContext
}

// The member-defaults pickers of the team update dialog. Pure presentation:
// every value and rule it renders is derived in useTenantDefaultsValues,
// which the submitting form also reads.
function TenantDefaultsFields(props: TenantDefaultsFieldsProps) {
  const { context } = props

  return (
    <>
      <p className="text-xs text-muted-foreground">
        What a user joining this team starts with. Members already in the team
        keep what they have.
      </p>

      <div className="space-y-2">
        <Label htmlFor="tenantDefaultProject">Default project</Label>
        <Select
          value={context.effectiveProjectId ?? NO_PROJECT_VALUE}
          disabled={context.pending}
          onValueChange={(next) => {
            context.setProjectId(next === NO_PROJECT_VALUE ? null : next)
          }}
        >
          <Select.Trigger id="tenantDefaultProject" size="md">
            <Select.Value
              placeholder={
                context.pending ? "Loading projects..." : "No default"
              }
            />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value={NO_PROJECT_VALUE}>No default</Select.Item>
            {context.projects.map((project) => (
              <Select.Item key={project.Id} value={project.Id}>
                <ProjectOption
                  name={project.Name}
                  countryCode={project.CountryCode}
                  createdAt={project.CreatedAt}
                />
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        {!context.pending && (
          <p className="text-xs text-muted-foreground">
            {context.staleProject
              ? "This team's saved default project no longer exists. Pick another, or save to clear it."
              : describeProjectOutcome(
                  context.projects.length,
                  context.effectiveProjectId,
                )}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="tenantDefaultCountry">Default country</Label>
        <CountryPicker
          className="w-full"
          value={context.effectiveCountryCode}
          onValueChange={context.setCountryCode}
          disabled={context.pending || context.countryFollowsProject}
          emptyLabel="No default"
          placeholder="No default"
        />
        <p className="text-xs text-muted-foreground">
          {context.countryFollowsProject
            ? "Not used: a new member lands in the default project and takes that project's country."
            : "Used for a new member who joins without a project, and for a project an invite creates for this team. A project created in the console keeps the country its creator picked."}
        </p>
      </div>
    </>
  )
}

// The same rules the invite dialog states, from the team's side: what a
// new member gets once this is saved.
function describeProjectOutcome(
  projectCount: number,
  projectId: string | null,
) {
  if (projectId !== null) {
    return "Every new member starts in this project."
  }
  if (projectCount === 0) {
    return "This team has no projects. The next invite creates one and makes it the default."
  }
  if (projectCount === 1) {
    return "With no default set, new members start in this team's only project."
  }
  return `With no default set, new members pick from this team's ${projectCount} projects on first login.`
}

export default TenantDefaultsFields
